import { IImpersonationContext } from '@ViewModels';
import { StyleSheet, css } from 'aphrodite';
import * as React from 'react';
import { useGetMetaAdAccounts } from '../../../../queries/Ads/useGetMetaAdAccounts';
import { useGetMetaAdsProvider } from '../../../../queries/Ads/useGetMetaAdsProvider';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { AccountDetailsInfoField } from '../AccountDetailsInfoField';

const { colors, spacing, typography } = THEME;

interface IProps {
	impersonationContext: IImpersonationContext;
}

/**
 * Grid field that surfaces which Meta ad accounts the customer's provider is
 * currently connected to. Reads the provider's `adAccountIds` and cross-references
 * the BM-visible ad accounts to resolve human-readable names.
 */
export function ConnectedMetaAdAccountField({ impersonationContext }: IProps) {
	const { data: provider } = useGetMetaAdsProvider({ impersonationContext });
	const connectedIds = provider?.adAccountIds ?? [];
	const isConnected = !!provider?.enabled && connectedIds.length > 0;
	const { data: visibleAdAccounts } = useGetMetaAdAccounts({ impersonationContext, enabled: isConnected });

	const label = `Connected Ad Account${connectedIds.length === 1 ? '' : 's'}`;

	if (!isConnected) {
		return <AccountDetailsInfoField label={label}>—</AccountDetailsInfoField>;
	}

	const byId = new Map((visibleAdAccounts ?? []).map(a => [a.id, a]));
	return (
		<AccountDetailsInfoField label={label}>
			<div className={css(styles.list)}>
				{connectedIds.map(id => {
					const account = byId.get(id);
					return (
						<div key={id} className={css(styles.row)}>
							<span className={css(styles.name)}>{account?.name || '(unknown name)'}</span>
							<span className={css(styles.id)}>id: {id}</span>
						</div>
					);
				})}
			</div>
		</AccountDetailsInfoField>
	);
}

const styles = StyleSheet.create({
	list: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
	},
	row: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
	},
	name: {
		color: colors.text.main,
		fontWeight: typography.fontWeights.semibold,
	},
	id: {
		fontFamily: 'monospace',
		fontSize: typography.fontSizes.xs,
		color: colors.text.light,
	},
});
